import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import { applyTheme, DesignerTheme, getAppliedTheme } from "../../../../shared/utility/designer-theme";
import { NotImplemented } from "./_layout/not-implemented";
import { SidebarNav } from "./_layout/sidebar-nav";
import { DEFAULT_SLUG, IShowcaseEntry, SHOWCASE_REGISTRY, SLUG_ALIASES } from "./_registry";
import "./ui-kit-lab.scss";

declare let window: Window & { currentDomain: string };

type IUiKitLabProps = IWithContextProp & IWithHistoryProp;

const UiKitLabBase: React.FC<IUiKitLabProps> = (_props: IUiKitLabProps): JSX.Element => {
  const navigate = useNavigate();
  const params = useParams<{ slug?: string; domainName?: string }>();
  // The lab's toggle previews the kit in the other theme and hands the page back on unmount.
  // It deliberately saves nothing: the designer's saved theme is the Appearance menu's.
  const [theme, setTheme] = useState<DesignerTheme>(getAppliedTheme);

  const rawSlug: string = params.slug ?? "";
  const canonicalSlug: string = SLUG_ALIASES[rawSlug] ?? rawSlug;

  const activeEntry: IShowcaseEntry | undefined = useMemo(
    (): IShowcaseEntry | undefined =>
      SHOWCASE_REGISTRY.find((entry: IShowcaseEntry): boolean => entry.slug === canonicalSlug),
    [canonicalSlug]
  );

  const domain: string = params.domainName ?? window.currentDomain;

  useEffect((): (() => void) => {
    const previous: DesignerTheme = getAppliedTheme();
    // Through applyTheme, not a bare attribute write: this cleanup runs after the next route
    // has rendered, so its component icons were resolved against the lab's theme.
    return (): void => applyTheme(previous);
  }, []);

  useEffect((): void => {
    applyTheme(theme);
  }, [theme]);

  useEffect((): void => {
    if (!params.slug) {
      navigate(`/${domain}/ui-kit/${DEFAULT_SLUG}`, { replace: true });
      return;
    }
    if (canonicalSlug !== rawSlug && activeEntry) {
      navigate(`/${domain}/ui-kit/${canonicalSlug}`, { replace: true });
      return;
    }
    if (!activeEntry) {
      navigate(`/${domain}/ui-kit/${DEFAULT_SLUG}`, { replace: true });
    }
  }, [params.slug, rawSlug, canonicalSlug, activeEntry, domain, navigate]);

  const handleSelect = (slug: string): void => {
    navigate(`/${domain}/ui-kit/${slug}`);
  };

  const toggleTheme = (): void => {
    const next: DesignerTheme = theme === "light" ? "dark" : "light";
    setTheme(next);
  };

  const activeSlug: string = activeEntry?.slug ?? DEFAULT_SLUG;

  const renderCanvas = (): JSX.Element | null => {
    if (!activeEntry) return null;
    if (activeEntry.status === "todo") {
      return <NotImplemented title={activeEntry.title} componentName={activeEntry.componentName} />;
    }
    return activeEntry.render();
  };

  return (
    <div className="ui-kit-lab" data-theme={theme}>
      <SidebarNav activeSlug={activeSlug} onSelect={handleSelect} />
      <main className="ui-kit-lab__main">
        <div className="ui-kit-lab__topbar">
          <div className="ui-kit-lab__topbar-meta">
            <span className="ui-kit-lab__topbar-label">workspace</span>
            <span className="ui-kit-lab__topbar-value">ui-kit / {activeSlug}</span>
          </div>
          <button
            type="button"
            className="ui-kit-lab__theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          >
            <span className="ui-kit-lab__theme-key">theme</span>
            <span className="ui-kit-lab__theme-value">{theme === "light" ? "light · 01" : "dark · 02"}</span>
          </button>
        </div>
        <div className="ui-kit-lab__canvas">{renderCanvas()}</div>
      </main>
    </div>
  );
};

const wrapped = withContext(withHistory(withManagement(UiKitLabBase)));

export { wrapped as UiKitLab };
